import { toast } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { Column } from "@jsq/ds/layouts";
import {
  useCreatePaymentAccountMutation,
  useUpdatePaymentAccountMutation,
  useUpdatePicrPositionDetailsMutation,
} from "~/graphql/supergraphHooks";
import { ProfileNameFields } from "~/pages/Compliance/utils/types";
import { Profile } from "../../../PaymentInstructionChange/PositionsTable/types";
import { ComplianceCaseProfileType } from "../../types";
import { schema } from "../CreateBankAccountAction/schema";
import { FormValues } from "../CreateBankAccountAction/types";
import { CompliancePaymentAccountType } from "../EditBankAccountAction/types";
import CreateBankAccountInformationCard from "./CreateBankAccountInformationCard";
import { bankAccountInformationContainer } from "./styles_vanilla.css";
import {
  prepareInitialValues,
  prepareCreateRequestPayload,
  prepareEditRequestPayloadForPICR,
  prepareEditRequestPayload,
  prepareCreateRequestPayloadForPICR,
} from "./utils";

interface Props {
  caseId: number;
  programId?: number;
  positionId?: string;
  mode: "create" | "edit";
  compliancePaymentAccount?: CompliancePaymentAccountType;
  investingProfile?: ProfileNameFields | null;
  profiles?: ComplianceCaseProfileType[] | Profile[] | undefined;
  onExit: () => void;
  showBankAndCountry?: boolean;
  showingInModal?: boolean;
  label: string;
  createdProfile?: Profile | null;
}

const BankAccountForm = ({
  caseId,
  programId,
  positionId,
  mode,
  compliancePaymentAccount,
  investingProfile,
  profiles,
  onExit,
  showBankAndCountry = true,
  showingInModal,
  label,
  createdProfile,
}: Props) => {
  const initialValues = prepareInitialValues(
    mode,
    compliancePaymentAccount,
    showBankAndCountry,
    showingInModal,
  );

  const [createPaymentAccountMutation] = useCreatePaymentAccountMutation({
    refetchQueries: ["GetProfilesForCase", "GetComplianceCase"],
    awaitRefetchQueries: true,
    fetchPolicy: "network-only",
    onError: (e) => {
      toast.error(e.message);
      throw e;
    },
  });

  const [updatePaymentAccountMutation] = useUpdatePaymentAccountMutation({
    refetchQueries: ["GetProfilesForCase", "GetComplianceCase", "GetPaymentAccount"],
    awaitRefetchQueries: true,
    fetchPolicy: "network-only",
    onError: (e) => {
      toast.error(e.message);
      throw e;
    },
  });

  const [updatePicrPositionDetails] = useUpdatePicrPositionDetailsMutation({
    refetchQueries: ["GetPicrPositionDetails"],
    awaitRefetchQueries: true,
    fetchPolicy: "network-only",
    onError: (e) => {
      toast.error(e.message);
      throw e;
    },
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const isCreateMode = mode === "create";
      const successMessage = isCreateMode ? "Bank account created" : "Bank account updated";

      if (showingInModal) {
        const data = isCreateMode
          ? prepareCreateRequestPayloadForPICR(values)
          : prepareEditRequestPayloadForPICR(values, initialValues, compliancePaymentAccount);

        await updatePicrPositionDetails({
          variables: {
            data: {
              caseId,
              positionId: positionId ?? "",
              ...data,
            },
          },
        });
      } else {
        const data = isCreateMode
          ? prepareCreateRequestPayload(values)
          : prepareEditRequestPayload(values, initialValues, compliancePaymentAccount);

        if (isCreateMode) {
          await createPaymentAccountMutation({
            variables: { caseId, data },
          });
        } else {
          await updatePaymentAccountMutation({
            variables: {
              caseId,
              paymentAccountId: compliancePaymentAccount?.id ?? -1,
              data,
            },
          });
        }
      }

      toast.success(successMessage);
      onExit();
    } catch {
      // Error handling is done in the mutation options
    }
  };

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
      submitOnlyChangedValues={false}
    >
      <Column size="md" className={bankAccountInformationContainer}>
        <CreateBankAccountInformationCard
          investingProfile={investingProfile}
          profiles={profiles}
          onExit={onExit}
          submitButtonText={mode === "create" ? "Create" : "Save"}
          showingInModal={showingInModal}
          label={label}
          caseId={caseId}
          programId={programId}
          positionId={positionId}
          createdProfile={createdProfile}
        />
      </Column>
    </Form>
  );
};

export default BankAccountForm;
